import { Controller } from "@hotwired/stimulus"

// Phase 2.11 Tier 1 — Asset Register wide-modal trigger.
//
// Opens the asset detail page in a wide modal lazy-loaded via XHR.
// Lifecycle actions inside the modal POST, Rails redirects back to the
// asset's show page, the XHR follows the redirect, and we swap the
// modal body with the fresh HTML — so state changes happen in place
// without a full nav.
//
// Wiring (placed on a wrapper that contains both the trigger rows and
// the modal element):
//
//   <div data-controller="asset-detail-modal"
//        data-asset-detail-modal-modal-id-value="asset-detail">
//     <table>
//       <tr data-action="click->asset-detail-modal#open"
//           data-url="/projects/123/assets/456">...</tr>
//     </table>
//
//     <div id="asset-detail-modal" data-controller="modal"
//          data-modal-id-value="asset-detail">
//       <div data-asset-detail-modal-target="slot">…</div>
//     </div>
//   </div>
//
// On row click the controller resets the slot, opens the modal, fetches
// the row's URL with `Accept: text/html` + `X-Requested-With`, and
// injects the response. The Rails action sees `request.xhr?` and
// renders without the global layout. Form submits inside the slot are
// intercepted in the same way; the response (after Rails follows its
// own redirect_to) replaces the slot.
//
// Mirrors `risk_detail_modal_controller` so future tier work that adds
// an Asset detail tabs / mitigation-style sub-controller can reuse the
// same DOM contract.
export default class extends Controller {
  static targets = ["slot", "title", "openLink"]
  static values = { modalId: String, defaultTitle: { type: String, default: "Asset detail" } }

  connect() {
    this._onSubmit = this._onSubmit.bind(this)
    if (this.hasSlotTarget) {
      this.slotTarget.addEventListener("submit", this._onSubmit)
    }
    this._showPendingFlash()
  }

  disconnect() {
    if (this.hasSlotTarget) {
      this.slotTarget.removeEventListener("submit", this._onSubmit)
    }
  }

  open(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return

    const trigger = event.currentTarget
    const interactive = event.target.closest("button, input, select, textarea")
    if (interactive && interactive !== trigger) return

    const url = trigger.dataset.url
    if (!url) return
    event.preventDefault()
    this._openWith(url, trigger.dataset.title || this.defaultTitleValue)
  }

  close(event) {
    if (event) event.preventDefault()
    this._close()
  }

  _openWith(url, title) {
    this._currentUrl = url
    this._setTitle(title)
    if (this.hasOpenLinkTarget) this.openLinkTarget.href = url
    this._showLoading()
    document.dispatchEvent(new CustomEvent("modal:open-by-id", { detail: { id: this.modalIdValue } }))
    this._fetchInto(url)
  }

  _setTitle(title) {
    if (this.hasTitleTarget) this.titleTarget.textContent = title || this.defaultTitleValue
  }

  async _onSubmit(event) {
    const form = event.target.closest("form")
    if (!form) return
    if (form.dataset.assetDetailModalSkip === "true") return
    event.preventDefault()

    const method = (form.method || "post").toUpperCase()
    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    this._showLoading()

    // `new FormData(form)` is supposed to include form-associated controls
    // declared outside the form via the `form="<id>"` attribute, but the
    // behavior is unreliable when the form is injected via innerHTML into
    // an XHR-loaded modal slot. The handoff-signoff page uses outside-the-
    // form checkboxes for `asset_ids[]` and `allow_warranty_override` —
    // missing those silently corrupts the request (e.g. "no asset_ids"
    // means "all commissioned assets" server-side, regardless of which
    // boxes were checked). Defensively scoop them up here before submit.
    const formData = new FormData(form)
    if (form.id) {
      document
        .querySelectorAll(`[form="${form.id}"]`)
        .forEach((el) => {
          if (form.contains(el)) return
          if (el.disabled || !el.name) return
          if ((el.type === "checkbox" || el.type === "radio") && !el.checked) return
          formData.append(el.name, el.value)
        })
    }

    try {
      const res = await fetch(form.action, {
        method,
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": csrf || ""
        },
        body: formData,
        credentials: "same-origin",
        redirect: "follow"
      })
      // Treat 422 (validation failure) the same as a successful render:
      // Rails re-renders the form with the error banner inline, so swap
      // the slot HTML so the user sees the actual validation message
      // instead of a generic "Failed to load" error.
      if (!res.ok && res.status !== 422) throw new Error(`HTTP ${res.status}`)
      const html = await res.text()
      // The redirect chain may land on the index (e.g. delete /
      // dismiss). When the response URL is no longer an asset detail
      // path, close the modal and refresh the register.
      if (res.ok && res.url && !/\/assets\/\d+(\?|$)/.test(res.url) && !/\/assets\/new(\?|$)/.test(res.url)) {
        this._stashFlashFromHeaders(res)
        this._close()
        window.location.reload()
        return
      }
      this.slotTarget.innerHTML = html
      this._refreshTitleFromBody()
      this._showFlashFromHeaders(res)
      if (res.url) {
        this._currentUrl = res.url
        if (this.hasOpenLinkTarget) this.openLinkTarget.href = res.url
      }
    } catch (err) {
      console.error("[asset-detail-modal] submit failed", err)
      this._showError()
    }
  }

  async _fetchInto(url) {
    try {
      const res = await fetch(url, {
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const html = await res.text()
      this.slotTarget.innerHTML = html
      this._refreshTitleFromBody()
    } catch (err) {
      console.error("[asset-detail-modal] fetch failed", err)
      this._showError()
    }
  }

  _refreshTitleFromBody() {
    const h1 = this.slotTarget.querySelector("h1")
    if (h1 && h1.textContent.trim()) {
      this._setTitle(h1.textContent.trim().slice(0, 120))
    }
  }

  _showLoading() {
    this.slotTarget.innerHTML =
      `<div class="flex items-center justify-center py-10 text-[13px] text-gray-500">Loading…</div>`
  }

  _showError() {
    this.slotTarget.innerHTML =
      `<div class="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">Failed to load. Refresh and try again.</div>`
  }

  _close() {
    const modal = document.getElementById(`${this.modalIdValue}-modal`) ||
      document.querySelector(`[data-modal-id-value="${this.modalIdValue}"]`)
    modal?.querySelector("[data-action*='modal#close']")?.click()
  }

  // Pop a body-level toast for the flash messages echoed by the
  // server via X-Flash-Notice / X-Flash-Alert response headers. The
  // global flash partial only renders inside the layout, so the
  // layoutless XHR show response never carries it — we surface it here
  // instead so a successful create / update / lifecycle action shows
  // the same green confirmation banner users get on a full nav.
  _showFlashFromHeaders(res) {
    const notice = this._readFlashHeader(res, "X-Flash-Notice")
    const alert = this._readFlashHeader(res, "X-Flash-Alert")
    if (notice) this._showToast(notice, "notice")
    if (alert) this._showToast(alert, "alert")
  }

  // When the redirect chain lands on a non-detail page (delete /
  // dismiss → register reload), stash the message so the next page
  // load can display it after `_showPendingFlash`.
  _stashFlashFromHeaders(res) {
    const notice = this._readFlashHeader(res, "X-Flash-Notice")
    const alert = this._readFlashHeader(res, "X-Flash-Alert")
    if (notice) {
      sessionStorage.setItem("asset_detail_modal_flash",
        JSON.stringify({ message: notice, type: "notice" }))
    } else if (alert) {
      sessionStorage.setItem("asset_detail_modal_flash",
        JSON.stringify({ message: alert, type: "alert" }))
    }
  }

  _showPendingFlash() {
    const raw = sessionStorage.getItem("asset_detail_modal_flash")
    if (!raw) return
    sessionStorage.removeItem("asset_detail_modal_flash")
    try {
      const { message, type } = JSON.parse(raw)
      if (message) this._showToast(message, type)
    } catch { /* ignore */ }
  }

  _readFlashHeader(res, name) {
    const raw = res.headers.get(name)
    if (!raw) return null
    try {
      return decodeURIComponent(raw)
    } catch {
      return raw
    }
  }

  _showToast(message, type = "notice") {
    const cssType = (type === "alert" || type === "error") ? "alert" : "notice"
    const iconSvg = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = iconSvg
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
